import { useEffect, useRef } from "react";

/**
 * 경량 이벤트 시스템
 *
 * SubscriptionProvider에서 WebSocket 데이터를 수신하면 emit()으로 이벤트를 발행하고,
 * 각 페이지 컴포넌트는 useSubscriptionEvent() 훅으로 필요한 이벤트만 구독한다.
 *
 * 이벤트 타입:
 * - 'currentState' : 유저 상태 변경 (SUBSCRIBE_CURRENTSTATE)
 * - 'sign'         : 체결 데이터 (SUBSCRIBE_REAL_SIGN)
 * - 'userSetting'  : 유저 설정 변경 (SUBSCRIBE_USER_SETTING)
 * - 'banking'      : 입출금 요청 (SUBSCRIBE_REQUEST_BANKING)
 */

const listeners = new Map();

export const subscriptionEvents = {
  on(type, callback) {
    if (!listeners.has(type)) {
      listeners.set(type, new Set());
    }
    listeners.get(type).add(callback);

    // cleanup 함수 반환
    return () => {
      const set = listeners.get(type);
      if (set) {
        set.delete(callback);
        if (set.size === 0) listeners.delete(type);
      }
    };
  },

  emit(type, data) {
    const set = listeners.get(type);
    if (set) {
      set.forEach((callback) => callback(data));
    }
  },
};

/**
 * useSubscriptionEvent - 페이지 컴포넌트에서 구독 이벤트를 수신하는 훅
 *
 * callbackRef로 항상 최신 콜백을 유지하여 스테일 클로저 문제를 원천 차단한다.
 * eventType은 문자열 상수이므로 useEffect가 재실행되지 않는다.
 *
 * @param {string} eventType - 이벤트 타입 ('currentState' | 'sign' | 'userSetting' | 'banking')
 * @param {Function} callback - 이벤트 수신 시 호출될 콜백
 */
export const useSubscriptionEvent = (eventType, callback) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const handler = (data) => callbackRef.current(data);
    const cleanup = subscriptionEvents.on(eventType, handler);
    return cleanup;
  }, [eventType]);
};
